'use client';

import { useState, useCallback } from 'react';
import { LocaleLink } from '@/core/i18n';
import { ArrowRight, Search, CreditCard, Globe, Network, Workflow } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { usePageContent } from '@/core/i18n/content';
import { TEMPLATE_CHROME } from '@/core/i18n/content/template-chrome.content';
import type { TemplateChromeContent } from '@/core/i18n/content/template-chrome.types';
import { INTEGRATIONS_HUB_CONTENT } from '@/core/i18n/content/integrations-hub.content';
import type { IntegrationsHubContent, IntegrationItem, IntegrationCategory } from '@/core/i18n/content/integrations-hub.types';
import { useTranslation } from '@/core/i18n';

/* -------------------------------------------------------------------------- */
/*  Data                                                                      */
/* -------------------------------------------------------------------------- */

type FilterTab = 'all' | 'payment' | 'ota' | 'gds' | 'automation';

const TAB_ICONS: Record<FilterTab, typeof Globe> = {
  all: Search,
  payment: CreditCard,
  ota: Globe,
  gds: Network,
  automation: Workflow,
};

const TAB_KEYS: FilterTab[] = ['all', 'payment', 'ota', 'gds', 'automation'];

const CATEGORY_COLORS: Record<IntegrationCategory, string> = {
  payment: 'bg-hms-green/10 text-hms-green',
  ota: 'bg-hms-blue/10 text-hms-blue',
  gds: 'bg-hms-gold/10 text-hms-gold',
  automation: 'bg-hms-teal/10 text-hms-teal',
};

const LETTER_COLORS: Record<IntegrationCategory, string> = {
  payment: 'bg-hms-green/15 text-hms-green border-hms-green/20',
  ota: 'bg-hms-blue/15 text-hms-blue border-hms-blue/20',
  gds: 'bg-hms-gold/15 text-hms-gold border-hms-gold/20',
  automation: 'bg-hms-teal/15 text-hms-teal border-hms-teal/20',
};

/* -------------------------------------------------------------------------- */
/*  Hero                                                                      */
/* -------------------------------------------------------------------------- */

function Hero({ c }: { c: IntegrationsHubContent }) {
  const { t } = useTranslation();
  return (
    <section id="integrations-hub-hero" className="relative w-full overflow-hidden bg-hms-bg">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 30%, rgba(200,145,58,0.08) 0%, transparent 70%)' }}
      />
      <div className="relative mx-auto max-w-4xl px-4 pb-16 pt-24 text-center sm:px-6 sm:pt-32 lg:px-8 lg:pt-40">
        <span className="mb-4 inline-block rounded-full border border-hms-gold/20 bg-hms-gold/5 px-4 py-1.5 text-xs font-semibold tracking-wide text-hms-gold">
          {c.badge}
        </span>
        <h1
          id="integrations-hub-title"
          className={cn(
            'font-poppins text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl lg:text-6xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {c.title}
        </h1>
        <p className="mx-auto mt-6 max-w-2xl text-base text-hms-textSub sm:text-lg">
          {c.subtitle}
        </p>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Integration Card                                                          */
/* -------------------------------------------------------------------------- */

function IntegrationCard({ item, t }: { item: IntegrationItem; t: TemplateChromeContent }) {
  const firstLetter = item.name.charAt(0).toUpperCase();
  return (
    <LocaleLink
      id={`integrations-hub-card-${item.slug}`}
      href={`/integrations/${item.slug}`}
      className={cn(
        'group flex flex-col rounded-xl border border-hms-border bg-hms-card p-6',
        'transition-all duration-200 hover:border-hms-gold/30 hover:shadow-lg hover:shadow-hms-gold/5',
      )}
    >
      <div className="mb-4 flex items-center gap-4">
        <div
          className={cn(
            'flex h-12 w-12 shrink-0 items-center justify-center rounded-full border text-lg font-bold',
            LETTER_COLORS[item.category],
          )}
        >
          {firstLetter}
        </div>
        <div className="min-w-0">
          <h3
            id={`integrations-hub-name-${item.slug}`}
            className="truncate text-base font-semibold text-hms-text"
          >
            {item.name}
          </h3>
          <span
            className={cn(
              'mt-1 inline-block rounded-full px-2.5 py-0.5 text-[11px] font-medium',
              CATEGORY_COLORS[item.category],
            )}
          >
            {item.type}
          </span>
        </div>
      </div>
      <p className="mb-4 flex-1 text-sm leading-relaxed text-hms-textSub">
        {item.description}
      </p>
      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-hms-gold transition-colors group-hover:text-hms-goldLt">
        {t.cta.learnMore}
        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
      </span>
    </LocaleLink>
  );
}

/* -------------------------------------------------------------------------- */
/*  Main Component                                                            */
/* -------------------------------------------------------------------------- */

export function IntegrationsHub() {
  const t = usePageContent(TEMPLATE_CHROME);
  const c = usePageContent(INTEGRATIONS_HUB_CONTENT);
  const [activeTab, setActiveTab] = useState<FilterTab>('all');

  const handleTabChange = useCallback((tab: FilterTab) => {
    setActiveTab(tab);
  }, []);

  const filtered = activeTab === 'all'
    ? c.integrations
    : c.integrations.filter((i) => i.category === activeTab);

  return (
    <div id="integrations-hub-root" className="min-h-screen bg-hms-bg">
      <Hero c={c} />

      {/* Filter Tabs */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div
          id="integrations-hub-tabs"
          className="flex flex-wrap gap-2 border-b border-hms-border pb-4"
        >
          {TAB_KEYS.map((key) => {
            const Icon = TAB_ICONS[key];
            const isActive = activeTab === key;
            return (
              <button
                key={key}
                id={`integrations-hub-tab-${key}`}
                type="button"
                onClick={() => handleTabChange(key)}
                className={cn(
                  'inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200',
                  isActive
                    ? 'bg-hms-gold/10 text-hms-gold border border-hms-gold/20'
                    : 'text-hms-textSub hover:bg-hms-surface hover:text-hms-text border border-transparent',
                )}
              >
                <Icon className="h-4 w-4" />
                {c.tabs[key]}
              </button>
            );
          })}
        </div>
      </section>

      {/* Grid */}
      <section className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
        <div
          id="integrations-hub-grid"
          className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
        >
          {filtered.map((item) => (
            <IntegrationCard key={item.slug} item={item} t={t} />
          ))}
        </div>

        {filtered.length === 0 && (
          <p id="integrations-hub-empty" className="py-16 text-center text-hms-textDim">
            {c.emptyState}
          </p>
        )}
      </section>

      {/* CTA */}
      <section
        id="integrations-hub-cta"
        className="mx-auto max-w-4xl px-4 pb-24 pt-8 text-center sm:px-6 lg:px-8"
      >
        <div className="rounded-xl border border-hms-border bg-hms-card p-8 sm:p-12">
          <h2 className="text-2xl font-bold text-hms-text sm:text-3xl">
            {c.cta.title}
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-hms-textSub sm:text-base">
            {c.cta.subtitle}
          </p>
          <LocaleLink
            id="integrations-hub-cta-contact"
            href="/contact"
            className={cn(
              'mt-6 inline-flex items-center gap-2 rounded-lg bg-hms-gold px-8 py-3.5',
              'text-sm font-semibold tracking-wide text-hms-bg shadow-lg shadow-hms-gold/20',
              'transition-all duration-200 hover:bg-hms-goldLt hover:shadow-hms-gold/30 active:scale-[0.98]',
            )}
          >
            {t.cta.contactBtn}
            <ArrowRight className="h-4 w-4" />
          </LocaleLink>
        </div>
      </section>
    </div>
  );
}
